import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet } from "@/components/ui/sheet";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Toolbar } from "@/components/ui/toolbar";
import { FormSection, Field } from "@/components/ui/form-section";
import { DataTable, type Column } from "@/components/ui/data-table";
import { PageHeader } from "@/components/ui/page-header";
import { useAriApps, useCreateAriApp, useUpdateAriApp, useDeleteAriApp } from "@/hooks/use-ari-apps";
import type { AriApp } from "@/types";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const columns: Column<AriApp>[] = [
  { key: "name", header: "Name" },
  { key: "allowed_origins", header: "Allowed Origins" },
  { key: "description", header: "Description" },
];

export default function AriAppsPage() {
  const { data, isLoading, error, refetch, isFetching } = useAriApps();
  const createMut = useCreateAriApp();
  const updateMut = useUpdateAriApp();
  const deleteMut = useDeleteAriApp();

  const defaultApp = { name: "", password: "", allowed_origins: "*", description: null as string | null };
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AriApp | null>(null);
  const [form, setForm] = useState(defaultApp);
  const [query, setQuery] = useState("");
  const [pendingDelete, setPendingDelete] = useState<AriApp | null>(null);

  const list = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((a) => [a.name, a.description].some((v) => v?.toLowerCase().includes(q)));
  }, [list, query]);

  const openCreate = () => { setEditing(null); setForm(defaultApp); setOpen(true); };
  const openEdit = (a: AriApp) => {
    setEditing(a);
    setForm({ name: a.name, password: a.password, allowed_origins: a.allowed_origins, description: a.description ?? null });
    setOpen(true);
  };

  const handleSave = () => {
    if (editing) {
      updateMut.mutate({ id: editing.id, data: form as Partial<typeof form> }, { onSuccess: () => { setOpen(false); toast.success("ARI app updated"); }, onError: (e) => toast.error(`Failed: ${(e as Error).message}`) });
    } else {
      createMut.mutate(form as typeof form, { onSuccess: () => { setOpen(false); toast.success("ARI app created"); }, onError: (e) => toast.error(`Failed: ${(e as Error).message}`) });
    }
  };

  const updateField = (field: string, value: unknown) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <div className="space-y-4">
      <PageHeader title="ARI Apps" description="Asterisk REST Interface application registrations" />

      <Toolbar
        search={{ value: query, onChange: setQuery, placeholder: "Search ARI apps..." }}
        count={`${filtered.length}${query ? ` of ${list.length}` : ""}`}
        onRefresh={() => refetch()}
        refreshing={isFetching}
        actions={<Button onClick={openCreate} size="sm"><Plus size={14} /> Create ARI App</Button>}
      />

      <DataTable
        columns={columns}
        data={filtered}
        isLoading={isLoading}
        getRowKey={(a) => a.id}
        onEdit={openEdit}
        onDelete={setPendingDelete}
        emptyMessage={query ? "No matches for your search." : "No ARI apps configured."}
        error={error}
        onRetry={() => refetch()}
      />

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Edit ARI App" : "Create ARI App"}
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={createMut.isPending || updateMut.isPending}>{editing ? "Update" : "Create"}</Button>
          </>
        }
      >
        <FormSection title="Application" columns={2}>
          <Field label="Name" required htmlFor="name" info="ARI application identifier. Referenced by Stasis dialplan actions. Example: my-ivr-app, call-router.">
            <Input id="name" value={form.name} onChange={(e) => updateField("name", e.target.value)} />
          </Field>
          <Field label="Password" required htmlFor="password" info="Password for ARI WebSocket authentication. Used by external applications connecting to Asterisk ARI.">
            <Input id="password" type="password" value={form.password} onChange={(e) => updateField("password", e.target.value)} />
          </Field>
          <Field label="Allowed Origins" htmlFor="allowed_origins" className="sm:col-span-2" info="CORS allowed origins for ARI HTTP requests. Default * allows all. Restrict in production: https://myapp.com.">
            <Input id="allowed_origins" value={form.allowed_origins} onChange={(e) => updateField("allowed_origins", e.target.value)} />
          </Field>
          <Field label="Description" htmlFor="description" className="sm:col-span-2" info="Human-readable description of the ARI application purpose.">
            <Textarea id="description" value={form.description ?? ""} onChange={(e) => updateField("description", e.target.value || null)} rows={2} />
          </Field>
        </FormSection>
      </Sheet>

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        title="Delete ARI app"
        description={<>Delete <strong>{pendingDelete?.name}</strong>? This cannot be undone.</>}
        loading={deleteMut.isPending}
        onConfirm={() =>
          pendingDelete &&
          deleteMut.mutate(pendingDelete.id, {
            onSuccess: () => { toast.success("ARI app deleted"); setPendingDelete(null); },
            onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
          })
        }
      />
    </div>
  );
}
